import { HttpClient, HttpParams, HttpBackend } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Observable, map, of, catchError } from 'rxjs';
import { environment } from 'environments/environment';
import { AutoLoading } from 'app/core/decorators/AutoLoading.decorator';

interface GeocodeResult {
  formatted_address: string;
  geometry: { location: { lat: number; lng: number } };
  address_components: {
    long_name: string;
    short_name: string;
    types: string[];
  }[];
}
interface GeocodeResponse {
  status: string;
  results: GeocodeResult[];
}
export interface GeocodedAddress {
  zipCode?: string;
  state?: string;
  city?: string;
  neighborhood?: string;
  street?: string;
  latitude?: number;
  longitude?: number;
}

@Injectable({ providedIn: 'root' })
export class GoogleGeocodingService {
  private readonly endpoint =
    'https://maps.googleapis.com/maps/api/geocode/json';
  private httpNoInterceptors: HttpClient;
  injector = inject(Injector);

  constructor(handler: HttpBackend) {
    this.httpNoInterceptors = new HttpClient(handler);
  }

  @AutoLoading()
  geocodeByCep(cep: string): Observable<GeocodedAddress | null> {
    const cleanCep = (cep || '').replace(/\D/g, '');
    if (cleanCep.length !== 8) return of(null);

    const params = new HttpParams()
      .set('components', `country:BR|postal_code:${cleanCep}`)
      .set('key', environment.googleMapsApiKey);

    return this.httpNoInterceptors
      .get<GeocodeResponse>(this.endpoint, { params })
      .pipe(
        map(res => {
          if (res.status !== 'OK' || !res.results?.length) return null;

          const result = res.results[0];
          const location = result.geometry.location;

          const get = (type: string) =>
            result.address_components.find(c => c.types.includes(type));

          const street = get('route')?.long_name;
          const neighborhood =
            get('sublocality')?.long_name ||
            get('sublocality_level_1')?.long_name ||
            get('political')?.long_name;
          const city =
            get('locality')?.long_name ||
            get('administrative_area_level_2')?.long_name;
          const state = get('administrative_area_level_1')?.short_name;
          const postal = get('postal_code')?.long_name;

          return {
            zipCode: postal || cleanCep,
            state,
            city,
            neighborhood,
            street,
            latitude: location.lat,
            longitude: location.lng,
          } as GeocodedAddress;
        }),
        catchError(() => of(null)),
      );
  }
}
